import * as THREE from 'three';
import DistanceTool from "./DistanceTool";
import BaseTool, { ToolUIRenderInfo, ToolLabel, ToolHandle } from './BaseTool';
import Input from '../Input';
import Engine from '../Engine';
import RemoveToolAction from "../../actions/tools/RemoveToolAction";
import { ToolTypes, ToolState, RenderLayers, CustomEventTypes } from "../../globals";
import store from '../../state/store';
import WorkspaceManager from '../WorkspaceManager';
import { assert } from '../utilities/ErrorHandling';
import SceneObjectUtils from '../utilities/SceneObjectUtils';
import { KeyCodes, MouseButtons } from '../../interfaces/Input';

const tempVector = new THREE.Vector3();

export default class ToolsManager
{
	private raycaster : THREE.Raycaster;
	private toolsRoots : Map<string, THREE.Object3D>;
	private activeTool : BaseTool | null;
	private hoveredTool : BaseTool | null;
	private busy : boolean;

	constructor()
	{
		this.raycaster = new THREE.Raycaster();
		this.raycaster.layers.enable(RenderLayers.MainCameraOnly);

		this.toolsRoots = new Map();
		this.activeTool = null;
		this.hoveredTool = null;
		this.busy = false;

		document.addEventListener(CustomEventTypes.simulationStarted, (e) =>
		{
			if (this.activeTool)
			{
				this.activeTool.forceFinalize();
				this.setActiveTool(null);
			}
			this.setHoveredTool(null);
			for (const root of this.toolsRoots.values())
			{
				root.visible = false;
			}
		});
		document.addEventListener(CustomEventTypes.simulationStopped, (e) =>
		{
			for (const root of this.toolsRoots.values())
			{
				root.visible = true;
			}
		});
	}
	
	clearData()
	{
		for (const root of this.toolsRoots.values())
		{
			for(const tool of root.children as BaseTool[])
			{
				SceneObjectUtils.disposeObject(tool, false);
			}
		}
		
		this.toolsRoots.clear();
		this.activeTool = null;
		this.hoveredTool = null;
		this.busy = false;
	}

	onSceneChange()
	{
		if (this.activeTool) this.activeTool.forceFinalize();
		this.setActiveTool(null);
		this.setHoveredTool(null);
	}

	isBusy() : boolean
	{
		return this.busy;
	}

	getSceneTools() : BaseTool[]
	{
		if (Engine.getInstance().getCurrentScene())
		{
			const toolsRoot = this.toolsRoots.get(Engine.getInstance().getCurrentScene()?.userData.guid);
			if (toolsRoot && toolsRoot.visible)
			{
				return toolsRoot.children as BaseTool[];
			}
		}
		return [];
	}

	/**
	 * Creates a new tool of the specified type but does not add it to the scene
	 * @param toolType
	 */
	createTool(toolType: ToolTypes) : BaseTool
	{
		let tool: BaseTool;
		switch (toolType)
		{
			case ToolTypes.DistanceTool:
				tool = new DistanceTool();
				break;
			default:
				assert(false);
		}

		this.addTool(tool!);
		return tool!;
	}

	private ensureSceneToolsRootExists()
	{
		if (!this.toolsRoots.has(Engine.getInstance().getCurrentScene()?.userData.guid))
		{
			const sceneToolsRoot = new THREE.Object3D();
			sceneToolsRoot.name = "Tools Root";
			Engine.getInstance().getCurrentScene()!.add(sceneToolsRoot);
			this.toolsRoots.set(Engine.getInstance().getCurrentScene()!.userData.guid, sceneToolsRoot);
		}
	}

	/**
	 * Adds a tool to the scene
	 * @param tool
	 */
	addTool(tool: BaseTool) : void
	{
		this.ensureSceneToolsRootExists();

		const toolsRoot = this.toolsRoots.get(Engine.getInstance().getCurrentScene()!.userData.guid);
		assert(toolsRoot);
		toolsRoot?.add(tool);
		this.setActiveTool(tool);
	}

	removeTool(tool: BaseTool) : void
	{
		const toolsRoot = this.toolsRoots.get(Engine.getInstance().getCurrentScene()!.userData.guid);
		assert(toolsRoot);
		toolsRoot?.remove(tool);
		this.activeTool = null;
	}

	setActiveTool(tool?: BaseTool | null) : void
	{
		if (this.activeTool === tool) return;
		if (this.activeTool)
		{
			this.activeTool.isActive = false;
		}
		this.activeTool = tool || null;
		if (this.activeTool)
		{
			this.activeTool.isActive = true;
		}

		this.setHoveredTool(null);
		if (!this.activeTool)
		{
			Engine.getInstance().getGizmoManager().setObjects(Engine.getInstance().getPickingManager().getCurrentSelection().objects);
		}
	}

	setHoveredTool(tool: BaseTool | null) : void
	{
		if (this.hoveredTool === tool) return;
		if (this.hoveredTool)
		{
			this.hoveredTool.setHighlightEnabled(false);
		}

		this.hoveredTool = tool;
		if (this.hoveredTool)
		{
			this.hoveredTool.setHighlightEnabled(true);
		}
	}

	getHoveredTool() : BaseTool | null
	{
		return this.hoveredTool;
	}

	isElementHovered(sceneViewId: string, elementRenderPosition: THREE.Vector2, elementWidth: number, elementHeight: number) : boolean
	{
		const sceneViewData = WorkspaceManager.getSceneViewData(sceneViewId);
		if (!elementRenderPosition || !sceneViewData?.context) return false;
		const rect = sceneViewData.context.canvas.getBoundingClientRect();
		const mousePos = Input.getMousePositionInRect(rect);
		mousePos.x = ((mousePos.x + 1) / 2) * rect.width;
		mousePos.y = (1 - ((mousePos.y + 1) / 2)) * rect.height;
		return (mousePos.x >= elementRenderPosition.x && mousePos.x <= elementRenderPosition.x + elementWidth &&
			mousePos.y >= elementRenderPosition.y && mousePos.y <= elementRenderPosition.y + elementHeight);
	}

	update() : void
	{
		this.busy = false;

		const hoveredWindowId = store.getState().workspaceModule.hoveredWindowId;
		const sceneViewData = WorkspaceManager.getSceneViewData(hoveredWindowId);
		if (sceneViewData && sceneViewData.context)
		{
			sceneViewData.context.canvas.style.cursor = 'default';
		}

		const toolsRoot = this.toolsRoots.get(Engine.getInstance().getCurrentScene()?.userData.guid);
		if (!toolsRoot)
		{
			return;
		}

		const camera = Engine.getInstance().getCamera();
		if(!camera) return;
		this.raycaster.setFromCamera(Input.getMousePosition(), camera);
		if (this.activeTool)
		{
			const preUpdateState = this.activeTool.state;

			if (preUpdateState === ToolState.Measure || preUpdateState === ToolState.Remeasure)
			{
				if (Input.getKeyDown(KeyCodes.ESCAPE))
				{
					new RemoveToolAction(this.activeTool);
					return;
				}
			}

			if (preUpdateState === ToolState.Finalized)
			{
				if (Input.getKeyDown(KeyCodes.DELETE) && Engine.getInstance().getPickingManager().getCurrentSelection().objects.length === 0)
				{
					new RemoveToolAction(this.activeTool);
					return;
				}
				else if (Input.getKeyDown(KeyCodes.ESCAPE))
				{
					this.setActiveTool(null);
					return;
				}

				for (const interactiveHandle of this.activeTool.UIRenderData.interactiveHandles)
				{
					if (this.isElementHovered(this.activeTool.sceneViewId!, interactiveHandle.renderPosition!,
						this.activeTool.getInteractiveHandleSize(),
						this.activeTool.getInteractiveHandleSize()))
					{
						this.activeTool.setInteractableHandleId(interactiveHandle.id);
						const sceneViewData = WorkspaceManager.getSceneViewData(hoveredWindowId);
						if (sceneViewData)
						{
							sceneViewData.context.canvas.style.cursor = 'move';
						}
					}
				}
			}
			this.activeTool.update();
			const postUpdateState = this.activeTool.state;
			if (preUpdateState === ToolState.Finalized && postUpdateState === ToolState.Finalized)
			{
				if (Input.getMouseButtonDown(MouseButtons.LEFT))
				{
					if (this.hoveredTool)
					{
						if (this.hoveredTool === this.activeTool)
						{
							this.setActiveTool(null);
						}
						else
						{
							this.setActiveTool(this.hoveredTool);
						}
						this.busy = true;
					}
				}
			}
			else
			{
				if (this.hoveredTool) this.setHoveredTool(null);
				this.busy = true;
			}

			if (this.activeTool && postUpdateState !== ToolState.Finalized)
			{
				Engine.getInstance().getGizmoManager().setObjects([]);
			}
			else if (preUpdateState !== ToolState.Finalized && postUpdateState === ToolState.Finalized)
			{
				Engine.getInstance().getGizmoManager().setObjects(Engine.getInstance().getPickingManager().getCurrentSelection().objects);
			}
		}

		if (this.hoveredTool)
		{
			let toolLabelsHovered = false;
			for (const label of this.hoveredTool.UIRenderData.labels)
			{
				if (!label.visible) continue;
				if (this.isElementHovered(this.hoveredTool.sceneViewId!, label.renderPosition!,
					this.hoveredTool.getLabelWidth(),
					this.hoveredTool.getLabelHeight()))
				{
					toolLabelsHovered = true;
				}
			}
			if (!toolLabelsHovered)
			{
				const intersections = this.raycaster.intersectObjects(this.hoveredTool.children, true);
				if (!intersections.length)
				{
					this.setHoveredTool(null);
					return;
				}
			}
			if (!this.activeTool || this.activeTool.state === ToolState.Finalized)
			{
				if (Input.getMouseButtonDown(MouseButtons.LEFT))
				{
					this.setActiveTool(this.hoveredTool);
					this.busy = true;
				}
			}
		}
		else if (!this.activeTool || this.activeTool.state === ToolState.Finalized)
		{
			for (const tool of toolsRoot.children as BaseTool[])
			{
				for (const label of tool.UIRenderData.labels)
				{
					if (!label.visible) continue;
					if (this.isElementHovered(tool.sceneViewId!, label.renderPosition!,
						tool.getLabelWidth(),
						tool.getLabelHeight()))
					{
						this.setHoveredTool(tool);
						this.busy = true;
						return;
					}
				}
			}

			const intersections = this.raycaster.intersectObjects(toolsRoot.children, true);
			if (intersections.length)
			{
				const tool = intersections[0].object.userData.parentTool;
				if (tool)
				{
					this.setHoveredTool(tool);
					this.busy = true;
				}
			}
		}
	}

	calculateScreenPositions(sceneViewId: string) : void
	{
		const intersectRect = (r1, r2) =>
		{
			return !(r2.left > r1.right ||
				r2.right < r1.left ||
				r2.top > r1.bottom ||
				r2.bottom < r1.top);
		}


		for (const tool of this.getSceneTools())
		{
			if (tool.sceneViewId !== sceneViewId) continue;

			if (tool instanceof DistanceTool)
			{
				const data = tool.UIRenderData;
				tool.setInteractableHandleId(0);
				for (const labelInfo of data.labels)
				{
					labelInfo.renderPosition = this.getProjectedPosition(tool.sceneViewId, labelInfo, tool.getLabelWidth(), tool.getLabelHeight());
				}
				for (const handleInfo of data.interactiveHandles)
				{
					handleInfo.renderPosition = this.getProjectedPosition(tool.sceneViewId, handleInfo, tool.getInteractiveHandleSize(), tool.getInteractiveHandleSize());
				}
			}
		}

		if (this.activeTool && this.activeTool.sceneViewId === sceneViewId)
		{
			//active tool? check if any of its labels overlap - if they do, recalculate positions for overlap mode (main stays where it is, x y z follow from top to bottom)
			if (this.activeTool instanceof DistanceTool)
			{
				const data = this.activeTool.UIRenderData;
				let isOverlapping = false;

				for (let i = 1; i < data.labels.length; i++)
				{
					const l1 = data.labels[0];
					const l2 = data.labels[i];
					if (l1.renderPosition !== undefined && l2.renderPosition !== undefined)
					{
						const r1 = {
							left: l1.renderPosition.x,
							right: l1.renderPosition.x + this.activeTool.getLabelWidth(),
							bottom: l1.renderPosition.y + this.activeTool.getLabelHeight(),
							top: l1.renderPosition.y
						};

						const r2 = {
							left: l2.renderPosition.x,
							right: l2.renderPosition.x + this.activeTool.getLabelWidth(),
							bottom: l2.renderPosition.y + this.activeTool.getLabelHeight(),
							top: l2.renderPosition?.y
						};

						if (intersectRect(r1, r2))
						{
							isOverlapping = true;
							break;
						}
					}
				}
				

				if (isOverlapping)
				{
					const mainLabelPosition = data.labels[0].renderPosition;
					let delta = this.activeTool.getLabelHeight();
					for (let i = 1; i < data.labels.length; i++)
					{
						const label = data.labels[i];
						if (label.renderPosition && mainLabelPosition)
						{
							label.renderPosition.x = mainLabelPosition.x;
							label.renderPosition.y = mainLabelPosition.y + delta;
							delta += this.activeTool.getLabelHeight();
						}
					}
				}
			}
		}
	}

	getProjectedPosition(sceneViewId: string, renderInfo: ToolUIRenderInfo, elementWidth: number, elementHeight: number) : THREE.Vector2 | undefined
	{
		if (renderInfo.visible)
		{
			const sceneViewData = WorkspaceManager.getSceneViewData(sceneViewId);
			if (sceneViewData?.context)
			{
				tempVector.set(renderInfo.position.x, renderInfo.position.y, renderInfo.position.z);
				tempVector.project(sceneViewData.activeCamera);
				if ((sceneViewData.activeCamera as THREE.PerspectiveCamera).isPerspectiveCamera)
				{
					if (tempVector.z >= 0 && tempVector.z <= 1) //within camera frustrum
					{
						const rect = sceneViewData.context.canvas.getBoundingClientRect();
						tempVector.x = ((tempVector.x + 1) / 2) * rect.width - (elementWidth / 2);
						tempVector.y = (1 - ((tempVector.y + 1) / 2)) * rect.height - (elementHeight / 2);
						return new THREE.Vector2(tempVector.x, tempVector.y);
					}
				}
				else
				{
					if (Math.abs(tempVector.z) <= 1)
					{
						const rect = sceneViewData.context.canvas.getBoundingClientRect();
						tempVector.x = ((tempVector.x + 1) / 2) * rect.width - (elementWidth / 2);
						tempVector.y = (1 - ((tempVector.y + 1) / 2)) * rect.height - (elementHeight / 2);
						return new THREE.Vector2(tempVector.x, tempVector.y);
					}
				}
			}
		}

		return undefined;
	}

	renderLabel(context, renderInfo: ToolLabel, labelWidth: number, labelHeight: number) : void
	{
		const cornerRadius = 30;
		context.beginPath();
		context.lineJoin = "round";
		context.lineWidth = cornerRadius;
		context.fillStyle = `rgb(${renderInfo.color.r * 255} , ${renderInfo.color.g * 255}, ${renderInfo.color.b * 255})`;
		context.strokeStyle = `rgb(${renderInfo.color.r * 255} , ${renderInfo.color.g * 255}, ${renderInfo.color.b * 255})`;
		context.strokeRect(renderInfo.renderPosition!.x + (cornerRadius / 2), renderInfo.renderPosition!.y + (cornerRadius / 2), labelWidth - cornerRadius, labelHeight - cornerRadius);
		context.fillRect(renderInfo.renderPosition!.x + (cornerRadius / 2), renderInfo.renderPosition!.y + (cornerRadius / 2), labelWidth - cornerRadius, labelHeight - cornerRadius);

		let fontsize = 20;
		context.textAlign = "center";
		context.font = `${fontsize}px Roboto`;
		let textWidth = context.measureText(renderInfo.text).width;
		while (textWidth > labelWidth - 20)
		{
			fontsize--;
			context.font = `${fontsize}px Roboto`;
			textWidth = context.measureText(renderInfo.text).width;
		}

		context.textAlign = "center";
		context.strokeStyle = 'black';
		context.lineWidth = 4;
		context.strokeText(renderInfo.text, renderInfo.renderPosition!.x + labelWidth / 2, 5 + renderInfo.renderPosition!.y + labelHeight / 2);
		context.fillStyle = `white`;
		context.fillText(renderInfo.text, renderInfo.renderPosition!.x + labelWidth / 2, 5 + renderInfo.renderPosition!.y + labelHeight / 2);
	}

	renderHandle(context, renderInfo: ToolHandle, radius: number) : void
	{
		context.beginPath();
		context.arc(renderInfo.renderPosition!.x + radius, renderInfo.renderPosition!.y + radius, radius, 0, 2 * Math.PI);
		context.fillStyle = 'black';
		context.fill();

		context.beginPath();
		context.arc(renderInfo.renderPosition!.x + radius, renderInfo.renderPosition!.y + radius, radius - radius * 0.1, 0, 2 * Math.PI);
		context.fillStyle = 'white';
		context.fill();

		context.beginPath();
		context.arc(renderInfo.renderPosition!.x + radius, renderInfo.renderPosition!.y + radius, radius - radius * 0.4, 0, 2 * Math.PI);
		context.fillStyle = `rgb(${renderInfo.color.r * 255} , ${renderInfo.color.g * 255}, ${renderInfo.color.b * 255})`;
		context.fill();
	}

	renderTool(tool : BaseTool) : void
	{
		if (tool instanceof DistanceTool)
		{
			const sceneViewData = WorkspaceManager.getSceneViewData(tool.sceneViewId);
			if (!sceneViewData?.context || !sceneViewData.context.canvas.clientHeight || !sceneViewData.context.canvas.clientWidth) return;
			const data = tool.getUIRenderData();
			for (const labelInfo of data.labels)
			{
				if (labelInfo.renderPosition)
				{
					this.renderLabel(sceneViewData.context, labelInfo, tool.getLabelWidth(), tool.getLabelHeight());
				}
			}
			for (const handleInfo of data.interactiveHandles)
			{
				if (handleInfo.renderPosition)
				{
					this.renderHandle(sceneViewData.context, handleInfo, tool.getInteractiveHandleSize() / 2);
				}
			}
		}
	}

	preRender(sceneViewId: string) : void
	{
		//hide tools that aren't part of the sceneView that is about to be rendered
		for (const tool of this.getSceneTools())
		{
			tool.visible = tool.sceneViewId === sceneViewId;
		}
	}

	render(sceneViewId: string) : void
	{
		this.calculateScreenPositions(sceneViewId);
		for (const tool of this.getSceneTools())
		{
			if (tool.sceneViewId === sceneViewId && tool !== this.activeTool) this.renderTool(tool);
		}
		//render active tool on top
		if (this.activeTool && this.activeTool.sceneViewId === sceneViewId) this.renderTool(this.activeTool);
	}
}
